import React, { useState, useRef, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  FiMenu,
  FiSearch,
  FiBell,
  FiSettings,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiMail,
  FiHelpCircle,
  FiSun,
  FiMoon,
  FiChevronRight,
  FiHome,
  FiCheckCircle,
  FiMessageSquare,
  FiTool
} from 'react-icons/fi';
import { FaBroadcastTower, FaBuilding, FaChartLine, FaMapMarkerAlt, FaProjectDiagram, FaShieldAlt, FaTachometerAlt, FaTag, FaUsers, FaUserTag, FaEye, FaUserCog, FaUpload, FaPlus, FaFileAlt, FaDraftingCompass } from 'react-icons/fa';
import { FaIndianRupeeSign } from "react-icons/fa6";
import { CiViewBoard } from "react-icons/ci";
import { IoCarSharp } from "react-icons/io5";
import { LuSettings } from 'react-icons/lu';
import { MdOutlinePaid } from 'react-icons/md';
import { AiOutlineFundProjectionScreen } from "react-icons/ai";
import { useAuth } from '../../provider/auth.provider';
import { useNotifications } from '../../provider/notification.provider';
import { Link, useNavigate } from 'react-router-dom';
import AttendanceModal from '../../components/attendance/AttendanceModal';

interface HeaderProps {
  onOpenMobileSidebar: () => void;
}

interface MenuItem {
  icon?: React.ComponentType<any>;
  label: string;
  path?: string;
  children?: MenuItem[];
  requiredPermission?: { resource: string; action: string };
  requiredAnyPermissions?: Array<{ resource: string; action: string }>;
  isHeading?: boolean;
  description?: string;
}

// Menu items - same as sidebar
const menu: MenuItem[] = [
  {
    icon: FiHome,
    label: 'Dashboard',
    path: '/dashboard',
    description: 'Overview of all activities and metrics'
  },
  {
    icon: FaTachometerAlt,
    label: 'Operations',
    isHeading: true
  },
  {
    icon: FaBroadcastTower,
    label: 'Survey',
    children: [
      {
        icon: FaEye,
        label: 'Working Sites',
        path: '/sites',
        requiredPermission: { resource: 'Site', action: 'view_my' },
        description: 'View all active working sites'
      },
      {
        icon: FaUserCog,
        label: 'Assign Sites',
        path: '/sites/assign',
        requiredPermission: { resource: 'Site', action: 'assign' },
        description: 'Assign sites to team members'
      },
      {
        icon: FaUserCog,
        label: 'Assign Request',
        path: '/sites/requested',
        requiredPermission: { resource: 'Site', action: 'site_requests_view' },
        description: 'Engineers\' requests to work a survey site'
      },
      {
        icon: FaUpload,
        label: 'Multiple Site Creation',
        path: '/sites/upload',
        requiredPermission: { resource: 'Site', action: 'upload' },
        description: 'Bulk upload and create multiple sites'
      },
      {
        icon: FaPlus,
        label: 'Single Site Creation',
        path: '/sites/create',
        requiredPermission: { resource: 'Site', action: 'create' },
        description: 'Create a new site individually'
      }
    ]
  },
  {
    icon: FaDraftingCompass,
    label: 'Designing',
    children: [
      {
        icon: FaProjectDiagram,
        label: 'Project Sites',
        path: '/designing/sites',
        requiredPermission: { resource: 'DesignSite', action: 'view_all' },
        description: 'Designing sites of the projects assigned to you'
      },
      {
        icon: FaEye,
        label: 'My Sites',
        path: '/designing/my-sites',
        requiredPermission: { resource: 'DesignSite', action: 'view_my' },
        description: 'Designing sites you created, or are the report maker or validator of'
      },
      {
        icon: FaUpload,
        label: 'Multiple Site Creation',
        path: '/designing/upload',
        requiredPermission: { resource: 'DesignSite', action: 'upload' },
        description: 'Bulk upload and create multiple Designing sites'
      },
      {
        icon: FaPlus,
        label: 'Single Site Creation',
        path: '/designing/create',
        requiredPermission: { resource: 'DesignSite', action: 'create' },
        description: 'Create a new Designing site individually'
      }
    ]
  },
  {
    icon: FaTachometerAlt,
    label: 'Expense Management',
    isHeading: true
  },
  {
    icon: AiOutlineFundProjectionScreen,
    label: 'Project Claims',
    path: '/claims/project',
    requiredPermission: { resource: 'project_claim', action: 'view_all' },
    description: 'Manage project-related expense claims'
  },
  {
    icon: CiViewBoard,
    label: 'General Claims',
    path: '/claims/general',
    requiredPermission: { resource: 'general_claim', action: 'view_all' },
    description: 'Handle general expense claims'
  },
  {
    icon: FaIndianRupeeSign,
    label: 'Advance',
    path: '/claims/advance',
    requiredAnyPermissions: [
      { resource: 'advance_claim', action: 'view_all' },
      { resource: 'advance_claim', action: 'submit' },
      { resource: 'advance_claim', action: 'approve' }
    ],
    description: 'Request and manage advance payments'
  },
  {
    icon: IoCarSharp,
    label: 'Cab Hiring',
    path: '/claims/cab-hiring',
    requiredPermission: { resource: 'cab_claim', action: 'read_all' },
    description: 'Book and manage cab services'
  },
  {
    icon: LuSettings,
    label: 'Billing Management',
    isHeading: true
  },
  {
    icon: AiOutlineFundProjectionScreen,
    label: 'Billings Done',
    path: '/billings',
    requiredPermission: { resource: 'Billing', action: 'view_stats' },
    description: 'View completed billings and invoices'
  },
  {
    icon: CiViewBoard,
    label: 'Billings Pending',
    path: '/billings/pending-sites',
    requiredPermission: { resource: 'Billing', action: 'view_eligible_sites' },
    description: 'Manage sites with pending billing'
  },
  {
    icon: LuSettings,
    label: 'Vendor Invoicing',
    isHeading: true
  },
  {
    icon: MdOutlinePaid,
    label: 'Vendor Invoicing',
    description: 'Vendor work acceptance, invoice acceptance, and purchase orders',
    children: [
      {
        icon: CiViewBoard,
        label: 'Vendor Work Acceptance',
        path: '/billings/vendor-work-acceptance/field-work',
        requiredPermission: { resource: 'VendorInvoicing', action: 'view_vendor_work_acceptance' },
        description: 'Accept vendor work and generate POs'
      },
      {
        icon: FaIndianRupeeSign,
        label: 'Vendor Invoice Acceptance',
        path: '/billings/vendor-invoice-acceptance',
        requiredPermission: { resource: 'VendorInvoicing', action: 'accept_vendor_invoice' },
        description: 'Book or reject vendor invoices'
      },
      {
        icon: AiOutlineFundProjectionScreen,
        label: 'PO Issued',
        path: '/billings/po-issued',
        requiredPermission: { resource: 'VendorInvoicing', action: 'view_po_issued' },
        description: 'Purchase orders issued to vendors'
      },
    ]
  },
  {
    icon: LuSettings,
    label: 'Attendance',
    isHeading: true
  },
  {
    icon: FaTachometerAlt,
    label: 'Attendance',
    description: 'Track and manage employee attendance',
    children: [
      {
        icon: FaEye,
        label: 'My',
        path: '/attendance/my',
        description: 'View your attendance records'
      },
      {
        icon: FaUsers,
        label: 'Team',
        path: '/attendance/team',
        requiredAnyPermissions: [
          { resource: 'attendance', action: 'view_team' },
          { resource: 'attendance', action: 'view_all' }
        ],
        description: 'View and manage your team attendance'
      },
      {
        icon: FaProjectDiagram,
        label: 'Project Team',
        path: '/attendance/project-team',
        requiredAnyPermissions: [
          { resource: 'attendance', action: 'view_project_team' },
          { resource: 'attendance', action: 'view_all' }
        ],
        description: 'View attendance for project-linked team members'
      },
      {
        icon: FaUsers,
        label: 'Team Member',
        path: '/attendance/project-team-members',
        requiredAnyPermissions: [
          { resource: 'attendance', action: 'view_project_team' },
          { resource: 'attendance', action: 'view_all' }
        ],
        description: 'View team members with the user list layout'
      },
      {
        icon: FaProjectDiagram,
        label: 'Working Schedules',
        path: '/attendance/work-schedules',
        requiredPermission: { resource: 'attendance_work_schedule', action: 'view_all' },
        description: 'Manage user weekly working days and shifts'
      }
    ]
  },
  {
    icon: FaMapMarkerAlt,
    label: 'Leaves',
    children: [
      {
        icon: FaMapMarkerAlt,
        label: 'Leave Application',
        path: '/leaves',
        requiredPermission: { resource: 'leave', action: 'view_all' },
        description: 'Manage employee leaves and time off'
      },
      {
        icon: FaProjectDiagram,
        label: 'Holiday Calendar',
        path: '/calendar',
        requiredPermission: { resource: 'calendar', action: 'view_all' },
        description: 'View holiday calendar'
      }
    ]
  },
  {
    icon: FiMessageSquare,
    label: 'Ticketing',
    isHeading: true
  },
  {
    icon: FiMessageSquare,
    label: 'Raise Ticket',
    path: '/tickets/raise',
    requiredPermission: { resource: 'ticket', action: 'view_my' },
    description: 'Create a support ticket and follow up'
  },
  {
    icon: FiTool,
    label: 'Ticket Actions',
    path: '/tickets/actions',
    requiredPermission: { resource: 'ticket', action: 'view_all' },
    description: 'Perform ticket addressing and status actions'
  },
  {
    icon: FaTachometerAlt,
    label: 'Reports',
    path: '/reports',
    requiredAnyPermissions: [
      { resource: 'reports', action: 'view_project_claim_report' },
      { resource: 'reports', action: 'view_cab_hiring_claim_report' },
      { resource: 'reports', action: 'view_general_claim_report' },
      { resource: 'reports', action: 'view_advance_claim_report' },
      { resource: 'reports', action: 'view_site_report' },
      { resource: 'reports', action: 'view_site_distance_report' },
      { resource: 'reports', action: 'view_site_distance_report_without_km' },
      { resource: 'reports', action: 'view_daily_travel_report' },
      { resource: 'reports', action: 'view_survey_workflow_report' },
      { resource: 'reports', action: 'view_users_report' },
      { resource: 'reports', action: 'view_login_report' },
      { resource: 'reports', action: 'view_password_report' },
      { resource: 'reports', action: 'view_permission_report' },
      { resource: 'reports', action: 'view_client_view_report' },
      { resource: 'reports', action: 'view_site_location_report' },
      { resource: 'reports', action: 'view_site_fw_start_report' },
      { resource: 'reports', action: 'view_reporting_checkin_report' },
      { resource: 'reports', action: 'view_user_productivity_report' },
    ],
    description: 'Open report sections'
  },
  {
    icon: FaUsers,
    label: 'HR Mgmt',
    isHeading: true
  },
  {
    icon: FaUserTag,
    label: 'User Details',
    path: '/hr/user-details',
    requiredPermission: { resource: 'user_details', action: 'view_all' },
    description: 'View and manage user extended details'
  },
  {
    icon: LuSettings,
    label: 'Masters',
    isHeading: true
  },
  {
    icon: FaUserTag,
    label: 'Users',
    path: '/team',
    requiredPermission: { resource: 'Team', action: 'view_all' },
    description: 'Manage team members and users'
  },
  {
    icon: FaUsers,
    label: 'Clients',
    path: '/clients',
    requiredPermission: { resource: 'Client', action: 'view_all' },
    description: 'Manage client information and contacts'
  },
  {
    icon: FaUsers,
    label: 'Vendor',
    path: '/vendor',
    requiredPermission: { resource: 'VendorManagement', action: 'view_all' },
    description: 'Manage vendor details and partnerships'
  },
  {
    icon: AiOutlineFundProjectionScreen,
    label: 'Projects',
    path: '/projects',
    requiredPermission: { resource: 'Project', action: 'view_all' },
    description: 'View and manage all projects'
  },
  {
    icon: FaShieldAlt,
    label: 'Roles',
    path: '/roles',
    requiredPermission: { resource: 'Role', action: 'view_all' },
    description: 'Configure user roles and permissions'
  },
  {
    icon: FaMapMarkerAlt,
    label: 'State Circles',
    path: '/states',
    requiredPermission: { resource: 'StateCircle', action: 'view_all' },
    description: 'Manage geographical state circles'
  },
  {
    icon: FaProjectDiagram,
    label: 'Activities',
    path: '/activities',
    requiredPermission: { resource: 'Activity', action: 'view_all' },
    description: 'Track and manage all activities'
  },
  {
    icon: FaTag,
    label: 'Designation',
    path: '/designations',
    requiredPermission: { resource: 'Designation', action: 'view_all' },
    description: 'Manage employee designations'
  },
  {
    icon: FaBuilding,
    label: 'Departments',
    path: '/departments',
    requiredPermission: { resource: 'Department', action: 'view_all' },
    description: 'Manage organization departments'
  },
  {
    icon: FaChartLine,
    label: 'Client Projects',
    path: '/client-projects',
    requiredPermission: { resource: 'ClientProject', action: 'view_all' },
    description: 'View client-specific projects'
  },
];

const Header: React.FC<HeaderProps> = ({ onOpenMobileSidebar }) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const { user, logout, hasPermission, attendance, onLeave } = useAuth();
  const { notifications, unreadCount, loading: notificationsLoading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [attendanceType, setAttendanceType] = useState<'check-in' | 'check-out'>('check-in');

  // Auto-open check-in modal if attendance is not marked
  useEffect(() => {
    if (user && !onLeave && !attendance?.check_in_time && hasPermission('attendance', 'portal_check_in')) {
      setAttendanceType('check-in');
      setIsAttendanceModalOpen(true);
    }
  }, [user, attendance, onLeave, hasPermission]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    if (!user) return [];

    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      const hasMenuPermission = (item: MenuItem): boolean => {
        const hasSinglePermission = item.requiredPermission
          ? hasPermission(item.requiredPermission.resource, item.requiredPermission.action)
          : false;
        const hasAnyPermission = item.requiredAnyPermissions?.some((permission) =>
          hasPermission(permission.resource, permission.action)
        ) ?? false;
        return hasSinglePermission || hasAnyPermission;
      };

      return items.filter(item => {
        // Always show headings
        if (item.isHeading) {
          return true;
        }

        // If no permission is required, show the item
        if (!item.requiredPermission && !item.requiredAnyPermissions?.length) {
          return true;
        }

        // Check if user has the required permission
        const hasRequiredPermission = hasMenuPermission(item);

        // If item has children, filter them too
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          // Show parent if it has permission or has visible children
          return hasRequiredPermission || filteredChildren.length > 0;
        }

        return hasRequiredPermission;
      }).map(item => {
        // If item has children, return it with filtered children
        if (item.children) {
          return {
            ...item,
            children: filterMenuItems(item.children)
          };
        }
        return item;
      });
    };

    return filterMenuItems(menu);
  }, [user, hasPermission]);

  // Flatten menu items for searching
  const searchableItems = useMemo(() => {
    const items: Array<MenuItem & { parentLabel?: string; headingLabel?: string }> = [];
    let currentHeading = '';

    filteredMenuItems.forEach(item => {
      // Track current heading
      if (item.isHeading) {
        currentHeading = item.label;
        return;
      }

      // Add items with path directly under headings
      if (!item.isHeading && item.path) {
        items.push({ ...item, headingLabel: currentHeading });
      }

      // Add children items
      if (item.children) {
        item.children.forEach(child => {
          if (child.path) {
            items.push({
              ...child,
              parentLabel: item.label,
              headingLabel: currentHeading
            });
          }
        });
      }
    });

    return items;
  }, [filteredMenuItems]);

  // Filter items based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return searchableItems.filter(item => {
      const labelMatch = item.label.toLowerCase().includes(query);
      const parentMatch = item.parentLabel?.toLowerCase().includes(query);
      const headingMatch = item.headingLabel?.toLowerCase().includes(query);
      const descriptionMatch = item.description?.toLowerCase().includes(query);
      return labelMatch || parentMatch || headingMatch || descriptionMatch;
    });
  }, [searchQuery, searchableItems]);

  // Helper function to format time ago
  const formatTimeAgo = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Check if notification is unread
  const isUnread = (status: string) => status !== 'read';

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSearchResults(true);
  };

  const handleSearchResultClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleSearchFocus = () => {
    if (searchQuery.trim()) {
      setShowSearchResults(true);
    }
  };

  return (
    <>
      <header className="bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-200/50 h-16 flex items-center justify-between px-6 sticky top-0 z-40">
        {/* Left Section */}
        <div className="flex items-center space-x-4 flex-1">
          {/* Mobile Menu Button */}
          <button
            onClick={onOpenMobileSidebar}
            className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-all duration-200 lg:hidden"
          >
            <FiMenu className="h-5 w-5" />
          </button>

          {/* Search Bar with Results */}
          <div className="hidden md:flex items-center relative flex-1 max-w-2xl" ref={searchRef}>
            <div className="relative w-full">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search menu items, features..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />

              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Search Results ({searchResults.length})
                    </p>
                  </div>
                  {searchResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSearchResultClick(item.path!)}
                      className="flex items-start px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0 group"
                    >
                      <div className="flex items-center justify-center w-10 h-10 mr-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors duration-150 flex-shrink-0 mt-0.5">
                        {item.icon && <item.icon className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {item.label}
                          </div>
                          {item.headingLabel && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {item.headingLabel}
                            </span>
                          )}
                        </div>
                        {item.parentLabel && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.parentLabel}
                          </div>
                        )}
                        {item.description && (
                          <div className="text-xs text-gray-400 mt-1 line-clamp-1">
                            {item.description}
                          </div>
                        )}
                      </div>
                      <FiChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2 group-hover:text-blue-600 transition-colors duration-150 mt-1" />
                    </div>
                  ))}
                </div>
              )}

              {/* No Results Message */}
              {showSearchResults && searchQuery.trim() && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 px-4 py-8 text-center">
                  <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <div className="text-gray-500 text-sm font-medium">
                    No results found for "{searchQuery}"
                  </div>
                  <div className="text-gray-400 text-xs mt-1">
                    Try searching with different keywords
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Search Button */}
          <button
            className="flex md:hidden items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-all duration-200"
            onClick={() => {
              // You can implement a mobile search modal here if needed
              toast('Mobile search coming soon!', { icon: '\uD83D\uDD0D' });
            }}
          >
            <FiSearch className="h-5 w-5" />
          </button>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-3">
          {/* Attendance Buttons */}
          {!onLeave && (
            <div className="hidden sm:flex items-center space-x-2">
              {!attendance?.check_in_time && hasPermission('attendance', 'portal_check_in') ? (
                <button
                  onClick={() => {
                    setAttendanceType('check-in');
                    setIsAttendanceModalOpen(true);
                  }}
                  className="flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded shadow transition-all duration-200"
                >
                  <FaPlus className="mr-1.5 h-3 w-3" />
                  Check In
                </button>
              ) : attendance?.check_in_time && !attendance?.check_out_time ? (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded border border-green-200">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    Checked In: {new Date(attendance.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {hasPermission('attendance', 'portal_check_out') && (
                    <button
                      onClick={() => {
                        setAttendanceType('check-out');
                        setIsAttendanceModalOpen(true);
                      }}
                      className="flex items-center px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded shadow transition-all duration-200"
                    >
                      <FiLogOut className="mr-1.5 h-3 w-3" />
                      Check Out
                    </button>
                  )}
                </div>
              ) : attendance?.check_in_time && attendance?.check_out_time ? (
                <div className="flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded border border-blue-200">
                  <FiCheckCircle className="mr-2 h-4 w-4" />
                  Attendance Marked
                </div>
              ) : null}
            </div>
          )}

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-all duration-200"
            >
              <FiBell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {isNotificationOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded shadow-2xl border border-gray-200/50 py-2 z-50 transform transition-all duration-200 origin-top-right">
                <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    <p className="text-xs text-gray-500">{unreadCount} unread</p>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllAsRead()}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notificationsLoading ? (
                    <div className="px-4 py-8 flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.slice(0, 5).map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => {
                          if (isUnread(notification.status)) markAsRead(notification.id);
                          setIsNotificationOpen(false);
                        }}
                        className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                          isUnread(notification.status) ? 'border-blue-500 bg-blue-50/30' : 'border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title || notification.subject || 'Notification'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                          </div>
                          <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
                            {formatTimeAgo(notification.sent_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-4 py-2 border-t border-gray-100">
                  <Link
                    to="/notifications"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    onClick={() => setIsNotificationOpen(false)}
                  >
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-100 rounded transition-all duration-200 group"
            >
              {/* Avatar */}
              <div className="relative">
                <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
              </div>

              {/* User Info (Hidden on small screens) */}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {user?.first_name || 'John Doe'}
                </p>
                <p className="text-xs text-gray-500 leading-tight">
                  {user?.role?.name || 'Administrator'}
                </p>
              </div>

              {/* Dropdown Arrow */}
              <FiChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''
                }`} />
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded shadow-2xl border border-gray-200/50 py-2 z-50 transform transition-all duration-200 origin-top-right">
                {/* User Info Header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-800 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {user?.first_name || 'John Doe'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user?.email || 'john.doe@company.com'}
                      </p>
                      <p className="text-xs text-blue-600 font-medium">
                        {user?.role?.name || 'Administrator'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiUser className="mr-3 h-4 w-4" />
                    My Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiSettings className="mr-3 h-4 w-4" />
                    Settings
                  </Link>
                  <Link
                    to="/messages"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiMail className="mr-3 h-4 w-4" />
                    Messages
                    <span className="ml-auto bg-red-100 text-red-600 text-xs font-medium px-2 py-1 rounded-full">
                      3
                    </span>
                  </Link>
                  <Link
                    to="/help"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiHelpCircle className="mr-3 h-4 w-4" />
                    Help & Support
                  </Link>
                </div>

                {/* Logout */}
                <div className="border-t border-gray-100 pt-2">
                  <button
                    onClick={() => {
                      setIsProfileDropdownOpen(false);
                      logout();
                    }}
                    className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <FiLogOut className="mr-3 h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={isAttendanceModalOpen}
        onClose={() => setIsAttendanceModalOpen(false)}
        type={attendanceType}
      />
    </>
  );
};

export default Header;
